import { CurrencyAmount } from 'app/core/models/sales-summary.model';

/** Below this, a non-empty stage would draw as an invisible sliver. */
const MIN_VISIBLE_SHARE = 2;

/**
 * Share of one funnel stage that advanced to the next, as a whole percent.
 *
 * Null when the earlier stage was empty: a conversion rate out of zero is
 * undefined, and rendering 0% or 100% there would be a claim the data cannot
 * support.
 *
 * @param from Count at the earlier stage.
 * @param to Count at the later stage.
 */
export function conversionPercent(from: number, to: number): number | null {
  if (!from || from <= 0) {
    return null;
  }
  return Math.round((to / from) * 100);
}

/**
 * Width of a stage's bar as a percentage of the cohort, floored so that a stage
 * with any deals in it stays visible.
 *
 * @param count Count at this stage.
 * @param cohort Count at the first stage.
 */
export function stageSharePercent(count: number, cohort: number): number {
  if (!cohort || cohort <= 0 || count <= 0) {
    return 0;
  }
  const share = Math.round((count / cohort) * 100);
  return Math.min(100, Math.max(MIN_VISIBLE_SHARE, share));
}

/** One transition between two funnel stages: how many stood at the earlier one, how many reached the next. */
export interface FunnelTransition {
  from: number;
  to: number;
}

/**
 * Index of the transition that lost the most deals, by ABSOLUTE count, or -1 when no
 * transition lost any.
 *
 * Absolute and not proportional: at this volume a rate is unstable — one quote that never
 * got approved is a 100% loss and a single deal, while nineteen opportunities that never
 * got a quote is the actual problem. Ties go to the earliest transition, where the cohort
 * is largest and a fix is worth most.
 *
 * @param transitions The funnel's transitions, in pouring order.
 */
export function biggestDropIndex(transitions: FunnelTransition[]): number {
  let worstIndex = -1;
  let worstLoss = 0;
  transitions.forEach((transition, index) => {
    const loss = transition.from - transition.to;
    if (loss > worstLoss) {
      worstLoss = loss;
      worstIndex = index;
    }
  });
  return worstIndex;
}

/**
 * The amount held in one currency, or null when that currency is absent — which
 * the cards render as a dash rather than as a zero.
 *
 * @param amounts Per-currency totals for a stage.
 * @param idCurrency The currency to read, or null when none was resolved.
 */
export function amountFor(amounts: CurrencyAmount[], idCurrency: number | null): number | null {
  if (idCurrency == null || !amounts?.length) {
    return null;
  }
  const match = amounts.find((entry) => entry.idCurrency === idCurrency);
  return match ? match.amount : null;
}

/**
 * Currency formatting shared by the three dashboard cards and the monthly
 * amount delta badge: cents included, degrading to a plain number when
 * `isoCode` is empty or malformed rather than throwing or guessing a
 * currency. An empty `isoCode` means no currency has been resolved yet
 * (e.g. a brand new instance's first day, before any quote exists) — not an
 * error, so the figure still matters more than its symbol.
 *
 * The fraction digits are deliberately NOT pinned to 2: `style: 'currency'`
 * already renders each currency's own minor unit, so USD gets two decimals and
 * a currency that has no cents (CLP, JPY) gets none. Forcing 2 everywhere would
 * invent centavos for the currencies that do not have them.
 *
 * They used to be forced to 0, which rounded a real 208.435,50 to "US$ 208.436"
 * — a figure that then failed to match the same total anywhere it is printed to
 * the cent, including the sales-order listing these cards link to.
 *
 * @param amount Amount to format.
 * @param lang Active i18n language.
 * @param isoCode Currency ISO code, or '' when none has been resolved yet.
 */
export function formatCurrencyAmount(amount: number, lang: string, isoCode: string): string {
  try {
    const parts = new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: isoCode,
    }).formatToParts(amount);

    // Reassembled with the symbol in front. Spanish puts it last — "208.168
    // US$" — which reads as a unit of measure appended to a number rather than
    // as money, and made every figure on the dashboard end in a different place
    // depending on its width. The separators still come from the locale; only
    // the symbol's position is ours.
    const symbol = parts
      .filter((part) => part.type === 'currency')
      .map((part) => part.value)
      .join('');
    const digits = parts
      .filter((part) => ['integer', 'group', 'decimal', 'fraction'].includes(part.type))
      .map((part) => part.value)
      .join('');
    // The sign leads the whole thing, never the digits: "-$6.100", not "$-6.100".
    const minus = parts.some((part) => part.type === 'minusSign') ? '-' : '';

    return symbol ? `${minus}${symbol} ${digits}` : `${minus}${digits}`;
  } catch {
    // No currency to read a minor unit off, so 2 is the assumption — the same
    // one every amount in the app is entered with. Pinned on both ends: the
    // decimal default would otherwise print 208.435,5 next to figures that
    // show two digits.
    return new Intl.NumberFormat(lang, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
}

/**
 * Formats an amount with its sign BEFORE the currency symbol ("-$6.100", not
 * "$-6.100"), which the default negative currency format does not guarantee.
 *
 * @param diff Signed difference to render.
 * @param lang Active i18n language.
 * @param isoCode Currency ISO code, or '' when none has been resolved yet.
 */
export function signedCurrency(diff: number, lang: string, isoCode: string): string {
  const magnitude = formatCurrencyAmount(Math.abs(diff), lang, isoCode);
  if (diff > 0) {
    return `+${magnitude}`;
  }
  if (diff < 0) {
    return `-${magnitude}`;
  }
  return magnitude;
}
